/**
 * useReorderSegmentsModal - Modal state management for segment reordering
 *
 * Manages the state for the segment reorder modal, which allows users to
 * drag and drop segments to reorder them within a switch step.
 *
 * Related:
 *   - components/ReorderSegmentsModal.tsx - The modal component
 *   - utils/segmentReordering.ts - Reorder logic
 *   - components/CanvasContextMenu.tsx - Opens the modal
 */

import { useCallback, useState } from 'react';
import type { Edge, Node } from '@xyflow/react';
import type { BaseNodeData } from '../types';
import type { SegmentNodeData } from '../types/stepNodes';
import { getOrderedSegments } from '../utils/segmentReordering';

/**
 * Information about a segment for display in the reorder modal
 */
export interface SegmentInfo {
	/** Unique identifier for the segment node */
	id: string;
	/** Zero-based position index of the segment */
	segmentIndex: number;
	/** Display name for the segment (e.g., "Segment #1") */
	name: string;
	/** Color used to identify this segment visually */
	caseColor: string;
	/** Summary of the filter criteria for this segment */
	filterSummary: string;
	/** Whether this is the default segment (cannot be moved) */
	isDefault: boolean;
}

/**
 * State for the reorder segments modal
 */
export interface ReorderSegmentsModalState {
	/** Whether the modal is currently open */
	isOpen: boolean;
	/** ID of the parent switch node whose segments are being reordered */
	parentSwitchNodeId: string | null;
	/** Ordered list of segments to display */
	segments: SegmentInfo[];
}

const INITIAL_STATE: ReorderSegmentsModalState = {
	isOpen: false,
	parentSwitchNodeId: null,
	segments: [],
};

/**
 * Get a human-readable summary of filter criteria
 */
function getFilterSummary(segmentData: SegmentNodeData): string {
	if (segmentData.isDefault) {
		return 'Everyone else';
	}

	const criteria = segmentData.primaryCriteria;
	if (!criteria) {
		return 'No filter';
	}

	if (criteria.value) {
		return criteria.value;
	}

	return 'Filter configured';
}

export interface UseReorderSegmentsModalReturn {
	/** Current modal state */
	state: ReorderSegmentsModalState;
	/** Opens the modal for a specific switch node */
	openModal: (switchNodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]) => void;
	/** Closes the modal */
	closeModal: () => void;
}

/**
 * Hook for managing the segment reorder modal state.
 *
 * @example
 * ```typescript
 * const { state, openModal, closeModal } = useReorderSegmentsModal();
 *
 * // Open modal from context menu
 * openModal(switchNodeId, nodes, edges);
 *
 * // In render:
 * <ReorderSegmentsModal
 *   isOpen={state.isOpen}
 *   onClose={closeModal}
 *   segments={state.segments}
 *   onReorder={handleReorder}
 * />
 * ```
 */
export function useReorderSegmentsModal(): UseReorderSegmentsModalReturn {
	const [state, setState] = useState<ReorderSegmentsModalState>(INITIAL_STATE);

	const openModal = useCallback((switchNodeId: string, nodes: Node<BaseNodeData>[], edges: Edge[]) => {
		const orderedSegments = getOrderedSegments(switchNodeId, nodes, edges);

		const segmentInfos: SegmentInfo[] = orderedSegments.map((segmentNode, index) => {
			const data = segmentNode.data;
			const segmentNumber = `Segment #${(data.segmentIndex ?? index) + 1}`;
			const displayName = data.segmentName ? `${segmentNumber}: ${data.segmentName}` : segmentNumber;
			return {
				id: segmentNode.id,
				segmentIndex: data.segmentIndex ?? index,
				name: displayName,
				caseColor: data.caseColor,
				filterSummary: getFilterSummary(data),
				isDefault: data.isDefault ?? false,
			};
		});

		setState({
			isOpen: true,
			parentSwitchNodeId: switchNodeId,
			segments: segmentInfos,
		});
	}, []);

	const closeModal = useCallback(() => {
		setState(INITIAL_STATE);
	}, []);

	return {
		state,
		openModal,
		closeModal,
	};
}
